import {
  TableContainer,
  Paper,
  TableHead,
  useTheme,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Box,
  Table,
  Typography,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as TemplateIcon,
  CheckCircleOutline as SelectedIcon,
  SearchOff as NoResultsIcon,
} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  downloadCheckListReport,
  searchChecklistTemplates,
  associateTemplateToSegment,
  deactivateChecklistTemplate,
} from "./api/checklistApi";
import { useLoading } from "components/common/context/LoadingContext";
import { useSnackbar } from "notistack";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import packageJson from "../../../../../../package.json";
import localStorageHelper from "../../../../../app/localStorage";
import { useCallback, useEffect, useState } from "react";
import { SegmentChecklist } from "components/QueueBoard/models/workOrderTypes";
import { ChecklistTemplate } from "./types/checklistTypes";
import { ChecklistTemplate as ChecklistTemplateComponent } from "./template";
import { saveChecklistTemplate, getChecklistTemplate } from "./template/api/checklistTemplateApi";
import { ChecklistTemplateRequest, ChecklistTemplateFullResponse } from "./template/types/checklistTemplateTypes";
import { ensureEditableWorkOrder } from "../../../utils/finalizationHelpers";

interface SegmentAssociatedChecklistProps {
  checkListCollection: SegmentChecklist[];
  segmentIdentity?: number;
  segmentName?: string;
  workOrderNumber?: string;
  onChecklistAdded?: (newChecklist: SegmentChecklist) => void;
  isReadOnly?: boolean;
  invoiceDocumentIdentity?: number;
}

const SegmentAssociatedChecklist = ({
  checkListCollection,
  segmentIdentity,
  segmentName,
  workOrderNumber,
  onChecklistAdded,
  isReadOnly = false,
  invoiceDocumentIdentity,
}: SegmentAssociatedChecklistProps) => {
  const { showLoading, hideLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );
  const getStatusChip = (isComplete: boolean) => (
    <Chip
      icon={isComplete ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
      label={isComplete ? "Complete" : "Draft"}
      color={isComplete ? "success" : "warning"}
      variant={isComplete ? "filled" : "outlined"}
      size="small"
      sx={{
        fontSize: "13px",
        fontWeight: 600,
        "& .MuiChip-icon": {
          fontSize: "13px",
        },
      }}
    />
  );

  const theme = useTheme();
  const handleChecklistDownload = async (
    attachmentIdentity: number,
    checklistName: string
  ) => {
    showLoading();
    try {
      // Generate filename with pattern: checklist name - SegmentNo - workOrderNumber - count
      // const segmentNo = segmentName || "Unknown";
      // const woNumber = workOrderNumber || "Unknown";
      const fileName = `${checklistName}.pdf`;

      await downloadCheckListReport(attachmentIdentity, fileName);
      enqueueSnackbar(`Checklist downloaded successfully`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(`Download error:.` + error, {
        variant: "error",
      });
    }
    hideLoading();

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleChecklistDownload",
      {
        elapsed_ms: "124",
        title: "Segment Checklist",
        componentName: "Segment-Associated-Checklist.tsx",
        version: packageJson.version,
      }
    );
  };

  // Template search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ChecklistTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    ChecklistTemplate[]
  >([]);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templateFormData, setTemplateFormData] =
    useState<ChecklistTemplateRequest | null>(null);
  const [templateFormHasErrors, setTemplateFormHasErrors] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTemplateData, setEditingTemplateData] = 
    useState<ChecklistTemplateFullResponse | null>(null);
  const [reopenSearchAfterEdit, setReopenSearchAfterEdit] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ChecklistTemplate | null>(null);
  const verifyEditable = useCallback(async () => {
    return ensureEditableWorkOrder(invoiceDocumentIdentity, enqueueSnackbar);
  }, [invoiceDocumentIdentity, enqueueSnackbar]);

  // Handle template creation success
  const handleTemplateCreated = () => {
    setIsTemplateDialogOpen(false);
    setIsEditMode(false);
    setEditingTemplateData(null);
    enqueueSnackbar("Template created successfully!", { variant: "success" });
  };

  // Handle edit template
  const handleEditTemplate = async (template: ChecklistTemplate) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }

    showLoading();
    try {
      const templateData = await getChecklistTemplate(template.identity);
      if (templateData) {
        setEditingTemplateData(templateData);
        setIsEditMode(true);
        setReopenSearchAfterEdit(true);
        setIsSearchDialogOpen(false);
        setIsTemplateDialogOpen(true);
      } else {
        enqueueSnackbar("Failed to load template data", { variant: "error" });
      }
    } catch (error) {
      console.error("Error loading template:", error);
      enqueueSnackbar(`Error loading template: ${error}`, { variant: "error" });
    } finally {
      hideLoading();
    }
  };

  // Handle delete template confirmation
  const handleDeleteClick = (template: ChecklistTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteConfirmOpen(true);
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    if (!templateToDelete || !user) return;

    showLoading();
    try {
      await deactivateChecklistTemplate(templateToDelete.identity, user.userName);
      enqueueSnackbar("Template has been removed successfully", { variant: "success" });
      setIsDeleteConfirmOpen(false);
      setTemplateToDelete(null);
      
      // Refresh the search results
      if (searchTerm.trim()) {
        const results = await searchChecklistTemplates(searchTerm.trim());
        setSearchResults(results);
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      enqueueSnackbar(`Error deleting template: ${error}`, { variant: "error" });
    } finally {
      hideLoading();
    }
  };

  // Handle template save (create or update)
  const handleSaveTemplate = async (templateData: ChecklistTemplateRequest) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    showLoading();
    const wasEditMode = isEditMode;
    const shouldReopenSearch = reopenSearchAfterEdit;
    try {
      await saveChecklistTemplate(templateData);
      const successMessage = wasEditMode 
        ? "Template updated successfully!" 
        : "Template created successfully!";
      enqueueSnackbar(successMessage, { variant: "success" });
      setIsTemplateDialogOpen(false);
      setTemplateFormData(null);
      setTemplateFormHasErrors(false);
      setIsEditMode(false);
      setEditingTemplateData(null);
      setReopenSearchAfterEdit(false);

      // Reopen search dialog if edit was initiated from there
      if (shouldReopenSearch && wasEditMode) {
        setIsSearchDialogOpen(true);
      }

      // Log amplitude event for template creation/update
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        wasEditMode ? "checklistTemplateUpdated" : "checklistTemplateCreated",
        {
          elapsed_ms: "124",
          title: wasEditMode ? "Checklist Template Update" : "Checklist Template Creation",
          componentName: "Segment-Associated-Checklist.tsx",
          version: packageJson.version,
        }
      );
    } catch (error) {
      console.error("Error saving template:", error);
      const errorMessage = wasEditMode 
        ? `Error updating template: ${error}` 
        : `Error creating template: ${error}`;
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Template search functions
  const handleSearchTemplates = async () => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    if (!searchTerm.trim() || !segmentIdentity) return;

    setIsSearching(true);
    try {
      const response = await searchChecklistTemplates(searchTerm.trim());
      setSearchResults(response);
      setIsSearchDialogOpen(true);
      //debugger;
      // Log amplitude event for template search
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "templateSearchPerformed",
        {
          elapsed_ms: "124",
          title: "Template Search",
          componentName: "Segment-Associated-Checklist.tsx",
          version: packageJson.version,
          //searchTerm: searchTerm.trim(),
          // resultsCount: response.templates.length,
        }
      );
    } catch (error) {
      enqueueSnackbar(`Error searching templates: ${error}`, {
        variant: "error",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleTemplateSelection = (template: ChecklistTemplate) => {
    if (isReadOnly) return;
    setSelectedTemplates((prev) => {
      const isSelected = prev.some((t) => t.identity === template.identity);
      if (isSelected) {
        return prev.filter((t) => t.identity !== template.identity);
      } else {
        return [...prev, template];
      }
    });
  };

  // Helper function to format date in MM/DD/YYYY HH:mm:ss format
  const formatLocalDateTime = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleAssociateTemplates = async () => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    if (selectedTemplates.length === 0 || !segmentIdentity) return;

    showLoading();
    try {
      const newChecklists = [];
      const currentDateTime = new Date().toISOString();

      for (let i = 0; i < selectedTemplates.length; i++) {
        const template = selectedTemplates[i];
        const existingChecklistCount = checkListCollection.length;
        const newChecklistNumber = existingChecklistCount + i + 1;
        const checklistObject: SegmentChecklist = {
          checklistIdentity: 0, // Will be set by the backend
          segmentIdentity: segmentIdentity,
          attachmentIdentity: null,
          checklistName:
            template.name +
            ` - ${segmentName} - ${workOrderNumber} - ${newChecklistNumber}`, // Pattern: Checklist + increment
          checklistModifiedBy: user.userName,
          checklistModifiedDate: currentDateTime,
          checklistEnteredDate: currentDateTime,
          checklistEnteredBy: user.userName,
          checklistIsActive: true,
          checklistIsComplete: false,
          templateIdentity: template.identity,
        };

        const newChecklist = await associateTemplateToSegment(checklistObject);
        newChecklist.checklistEnteredDate = formatLocalDateTime(new Date());
        newChecklists.push(newChecklist);

        // Call the callback to update parent component
        if (onChecklistAdded) {
          onChecklistAdded(newChecklist);
        }
      }

      enqueueSnackbar(
        `Successfully associated ${newChecklists.length} template(s) to segment`,
        { variant: "success" }
      );

      // Log amplitude event for template association
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "templatesAssociated",
        {
          elapsed_ms: "124",
          title: "Template Association",
          componentName: "Segment-Associated-Checklist.tsx",
          version: packageJson.version,
          //templatesCount: newChecklists.length,
          //segmentIdentity: segmentIdentity,
        }
      );

      // Reset state
      setSelectedTemplates([]);
      setSearchResults([]);
      setSearchTerm("");
      setIsSearchDialogOpen(false);
    } catch (error) {
      enqueueSnackbar(`Error associating templates: ${error}`, {
        variant: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleCloseSearchDialog = () => {
    setIsSearchDialogOpen(false);
    setSelectedTemplates([]);
    setSearchResults([]);
    setSearchTerm("");
  };

  useEffect(() => {
    if (isReadOnly) {
      setIsSearchDialogOpen(false);
      setIsTemplateDialogOpen(false);
      setSelectedTemplates([]);
    }
  }, [isReadOnly]);

  return (
    <Box>
      {/* Temporary Comment out */}
      {/* Template Search Section */}
      <Box
        sx={{
          mb: 2,
          p: 1,
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Search Templates
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search for checklist templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchTemplates()}
            sx={{ flexGrow: 1 }}
            disabled={isReadOnly}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearchTemplates}
            disabled={
              !searchTerm.trim() ||
              !segmentIdentity ||
              isSearching ||
              isReadOnly
            }
            sx={{ minWidth: 120, textTransform: "capitalize" }}
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setIsEditMode(false);
              setEditingTemplateData(null);
              setIsTemplateDialogOpen(true);
            }}
            sx={{ minWidth: 120, textTransform: "capitalize" }}
            disabled={isReadOnly}
          >
            Create
          </Button>
        </Box>
      </Box>

      {/* Template Search Dialog */}
      <Dialog
        open={isSearchDialogOpen && !isReadOnly}
        onClose={handleCloseSearchDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: theme.shadows[10],
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
              : `linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)`,
            color: "#fff",
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TemplateIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Select Templates
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {searchResults.length} template{searchResults.length !== 1 ? "s" : ""} found
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseSearchDialog}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {selectedTemplates.length > 0 && (
            <Chip
              icon={<SelectedIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
              label={`${selectedTemplates.length} selected`}
              size="small"
              sx={{
                mt: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {searchResults.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                px: 3,
              }}
            >
              <NoResultsIcon
                sx={{
                  fontSize: 64,
                  color: theme.palette.grey[400],
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 500, mb: 0.5 }}
              >
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                Try a different search term to find matching templates.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: "400px", overflow: "auto", p: 2 }}>
              {searchResults.map((template, index) => {
                const isSelected = selectedTemplates.some(
                  (t) => t.identity === template.identity
                );
                return (
                  <Paper
                    key={template.identity}
                    elevation={isSelected ? 3 : 0}
                    sx={{
                      mb: index < searchResults.length - 1 ? 1.5 : 0,
                      borderRadius: 2,
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      bgcolor: isSelected
                        ? theme.palette.mode === "light"
                          ? "rgba(33, 150, 243, 0.04)"
                          : "rgba(33, 150, 243, 0.08)"
                        : theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      overflow: "hidden",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2],
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        gap: 1,
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleTemplateSelection(template)}
                        disabled={isReadOnly}
                        sx={{
                          color: theme.palette.grey[400],
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                      
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: isSelected
                            ? theme.palette.primary.main
                            : theme.palette.mode === "light"
                              ? theme.palette.grey[100]
                              : theme.palette.grey[800],
                          flexShrink: 0,
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <TemplateIcon
                          sx={{
                            fontSize: 20,
                            color: isSelected
                              ? "#fff"
                              : theme.palette.grey[500],
                          }}
                        />
                      </Box>
                      
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => handleTemplateSelection(template)}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {template.name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!user.hasChecklistTemplateAccess) {
                              enqueueSnackbar(
                                `You do not have permission to edit templates`,
                                { variant: "error" }
                              );
                              return;
                            }
                            handleEditTemplate(template);
                          }}
                          disabled={isReadOnly}
                          sx={{
                            textTransform: "capitalize",
                            borderRadius: 1.5,
                            minWidth: "auto",
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.75rem",
                            borderColor: theme.palette.grey[300],
                            color: theme.palette.text.secondary,
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              bgcolor: "rgba(33, 150, 243, 0.04)",
                            },
                          }}
                        >
                          Edit
                        </Button>
                        <Tooltip
                          title={
                            !template.canDelete
                              ? "Cannot delete: Template has checklists in progress"
                              : "Delete template"
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!user.hasChecklistTemplateAccess) {
                                  enqueueSnackbar(
                                    `You do not have permission to delete templates`,
                                    { variant: "error" }
                                  );
                                  return;
                                }
                                handleDeleteClick(template);
                              }}
                              disabled={isReadOnly || !template.canDelete}
                              sx={{
                                borderRadius: 1.5,
                                border: `1px solid ${theme.palette.grey[300]}`,
                                color: template.canDelete
                                  ? theme.palette.error.main
                                  : theme.palette.grey[400],
                                "&:hover": {
                                  borderColor: theme.palette.error.main,
                                  bgcolor: "rgba(211, 47, 47, 0.04)",
                                },
                                "&.Mui-disabled": {
                                  borderColor: theme.palette.grey[200],
                                  color: theme.palette.grey[400],
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseSearchDialog}
            sx={{
              textTransform: "capitalize",
              px: 3,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!user.hasChecklistAssignmentAccess) {
                enqueueSnackbar(
                  `You do not have permission to perform this operation:`,
                  { variant: "error" }
                );
                return;
              }
              handleAssociateTemplates();
            }}
            variant="contained"
            disabled={selectedTemplates.length === 0 || isReadOnly}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "capitalize",
              px: 3,
              borderRadius: 2,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            Associate {selectedTemplates.length > 0 ? `(${selectedTemplates.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setTemplateToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: theme.shadows[10],
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
              : `linear-gradient(135deg, #5f1e1e 0%, #370d0d 100%)`,
            color: "#fff",
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DeleteIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Delete Template
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setTemplateToDelete(null);
              }}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to delete the template{" "}
            <strong style={{ color: theme.palette.text.primary }}>{templateToDelete?.name}</strong>?
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1.5, color: theme.palette.warning.main }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
            gap: 1,
          }}
        >
          <Button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setTemplateToDelete(null);
            }}
            sx={{ textTransform: "capitalize", px: 3, borderRadius: 2 }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              textTransform: "capitalize",
              px: 3,
              borderRadius: 2,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Creation/Edit Dialog */}
      <Dialog
        open={isTemplateDialogOpen && !isReadOnly}
        onClose={() => {
          const shouldReopenSearch = reopenSearchAfterEdit;
          setIsTemplateDialogOpen(false);
          setTemplateFormData(null);
          setTemplateFormHasErrors(false);
          setIsEditMode(false);
          setEditingTemplateData(null);
          setReopenSearchAfterEdit(false);
          if (shouldReopenSearch) {
            setIsSearchDialogOpen(true);
          }
        }}
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
            borderRadius: 2,
            width: { xs: "90%", sm: "700px" },
            maxWidth: "700px",
          },
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            m: 0,
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {isEditMode ? "Edit Checklist Template" : "New Checklist Template"}
            </Typography>
            <IconButton
              onClick={() => {
                const shouldReopenSearch = reopenSearchAfterEdit;
                setIsTemplateDialogOpen(false);
                setTemplateFormData(null);
                setTemplateFormHasErrors(false);
                setIsEditMode(false);
                setEditingTemplateData(null);
                setReopenSearchAfterEdit(false);
                if (shouldReopenSearch) {
                  setIsSearchDialogOpen(true);
                }
              }}
              size="small"
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ChecklistTemplateComponent
            onTemplateCreated={handleTemplateCreated}
            onFormDataChange={(data, hasErrors) => {
              setTemplateFormData(data);
              setTemplateFormHasErrors(hasErrors);
            }}
            initialData={editingTemplateData}
            isEditMode={isEditMode}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const shouldReopenSearch = reopenSearchAfterEdit;
              setIsTemplateDialogOpen(false);
              setTemplateFormData(null);
              setTemplateFormHasErrors(false);
              setIsEditMode(false);
              setEditingTemplateData(null);
              setReopenSearchAfterEdit(false);
              if (shouldReopenSearch) {
                setIsSearchDialogOpen(true);
              }
            }}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (templateFormData && !templateFormHasErrors) {
                if (!user.hasChecklistTemplateAccess) {
                  enqueueSnackbar(
                    `You do not have permission to perform this operation:`,
                    {
                      variant: "error",
                    }
                  );
                  return;
                }
                handleSaveTemplate(templateFormData);
              }
            }}
            variant="contained"
            disabled={!templateFormData || templateFormHasErrors}
            sx={{ textTransform: "capitalize" }}
          >
            {isEditMode ? "Update Template" : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* <Divider sx={{ my: 2 }} /> */}

      {/* Existing Checklists Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Associated Checklists
          {/*  ({checkListCollection.length}) */}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: (theme) => theme.shadows[10],
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#fff" : "#181F2A",
            minWidth: 650,
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead
              sx={{
                backgroundColor: theme.palette.primary.main,
                "& .MuiTableCell-head": {
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? "#2371E7"
                      : "rgba(0, 0, 0, 0.5)", //theme.palette.primary.main, // Ensure cells inherit the TableHead background
                  color:
                    theme.palette.mode === "light"
                      ? theme.palette.primary.contrastText
                      : "white", // Adjust text color for contrast
                },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  status
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Action
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  Creation Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checkListCollection.map((item, index) => (
                <TableRow
                  key={item.checklistIdentity}
                  sx={{
                    backgroundColor:
                      index % 2 === 0
                        ? theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.02)"
                          : "rgba(0, 0, 0, 0.02)"
                        : theme.palette.background.paper,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      transform: "scale(1.01)",
                      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.light}`,
                    },
                  }}
                >
                  {/* sx={{ py: 3 }} */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: 13 }}
                      >
                        {item.checklistName}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* sx={{ py: 3 }} */}
                  <TableCell align="center">
                    {getStatusChip(item.checklistIsComplete)}
                  </TableCell>
                  <TableCell>
                    {item.checklistIsComplete && (
                      <IconButton
                        onClick={() => {
                          handleChecklistDownload(
                            item.attachmentIdentity,
                            item.checklistName
                          );
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <CalendarIcon
                        sx={{
                          fontSize: "13px",
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: 13 }}
                      >
                        {item.checklistEnteredDate}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SegmentAssociatedChecklist;
